import { AuthClient } from "@icp-sdk/auth/client";
import type { HttpAgent, Identity } from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import type { ProtocolActor } from "@motokomaker/ic-client";
import { fetchWalletBalances } from "@motokomaker/ic-client";
import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { toast } from "sonner";
import { FIXTURE } from "@/data/fixtures";
import {
	getAuthenticatedActor,
	getConfig,
	getLastAttachError,
	hasProtocolEnv,
} from "@/lib/protocol-actor";
import {
	getIcConfig,
	resolveDerivationOrigin,
	resolveIdentityProvider,
} from "@/lib/network";
import { logRuntimeDiagnosticsOnce } from "@/lib/runtime-diagnostics";
import { sessionTtlNs } from "@/lib/session-prefs";

export type WalletBalances = {
	USDM: number;
	ICP: number;
	ckBTC: number;
	ckETH: number;
	MMK: number;
	ckUSDC: number;
};

/**
 * anonymous — not logged in
 * healthy — II + protocol actor ready
 * degraded — II present but actor/role broken (prompt once)
 * degraded_prompt — show reconnect modal
 * expired — II session dead; need full re-login
 */
export type SessionStatus =
	| "anonymous"
	| "healthy"
	| "degraded"
	| "degraded_prompt"
	| "expired";

export type ProtocolRole = "admin" | "user" | "guest" | "unknown";

const ZERO_BALANCES: WalletBalances = {
	USDM: 0,
	ICP: 0,
	ckBTC: 0,
	ckETH: 0,
	MMK: 0,
	ckUSDC: 0,
};

type AuthState = {
	isAuthenticated: boolean;
	isInitializing: boolean;
	isLoggingIn: boolean;
	principal: string | null;
	identity: Identity | null;
	agent: HttpAgent | null;
	actor: ProtocolActor | null;
	/** Live ICRC when env+auth; fixtures only offline (no protocol env). */
	balances: WalletBalances;
	balancesLive: boolean;
	balancesLoading: boolean;
	/** Last balance-fetch error (per-token failures may still leave partial zeros). */
	balancesError: string | null;
	/** Last protocol agent attach error (honest message, not only "env baked"). */
	actorError: string | null;
	sessionStatus: SessionStatus;
	protocolRole: ProtocolRole;
	refreshBalances: () => Promise<void>;
	/** Re-create agent/actor for current II session (after env/network recovery). */
	reconnectProtocol: () => Promise<boolean>;
	/**
	 * Ensure protocol actor+agent exist for the current identity.
	 * Updates context and returns the live pair (avoids React state race).
	 */
	ensureProtocolActor: () => Promise<{
		actor: ProtocolActor;
		agent: HttpAgent;
	} | null>;
	login: () => Promise<void>;
	logout: () => Promise<void>;
	configError: string | null;
	/** Raise session modal when vaults/writes fail with Unauthorized. */
	reportSessionIssue: (reason?: "expired" | "degraded" | "unauthorized") => void;
	dismissSessionPrompt: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

function formatLoginError(err: unknown): string {
	const raw = err instanceof Error ? err.message : String(err ?? "");
	const lower = raw.toLowerCase();
	if (
		lower.includes("failed to fetch") ||
		lower.includes("load failed") ||
		lower.includes("networkerror")
	) {
		return "Internet Identity could not finish (network / popup). Allow popups and retry.";
	}
	if (
		lower.includes("user interrupt") ||
		lower.includes("closed") ||
		lower.includes("abort")
	) {
		return "Login cancelled.";
	}
	return raw || "Internet Identity login failed";
}

function actorNullMessage(): string {
	const detail = getLastAttachError();
	try {
		const c = getIcConfig();
		const origin =
			typeof window !== "undefined" ? window.location.origin : "?";
		const base = `Protocol actor failed (origin=${origin} host=${c.host || "?"} protocol=${c.protocolCanisterId || "missing"}).`;
		const hint =
			" For local use http://127.0.0.1:8000/?canisterId=<assets> — not {id}.localhost (different II session). Logout, hard refresh, Connect again.";
		return detail ? `${base} ${detail}.${hint}` : `${base}${hint}`;
	} catch {
		return (
			detail ||
			"Connected, but protocol actor failed — check VITE_IC_HOST and protocol canister id."
		);
	}
}

function roleFromCandid(role: unknown): ProtocolRole {
	if (!role || typeof role !== "object") return "unknown";
	const key = Object.keys(role as object)[0];
	if (key === "admin" || key === "user" || key === "guest") return key;
	return "unknown";
}

async function fetchRole(actor: ProtocolActor): Promise<ProtocolRole> {
	if (typeof actor.getCallerUserRole !== "function") return "unknown";
	try {
		const role = await actor.getCallerUserRole();
		return roleFromCandid(role);
	} catch {
		return "unknown";
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	useEffect(() => {
		logRuntimeDiagnosticsOnce();
	}, []);

	const [authClient] = useState(() => {
		const config = getIcConfig();
		const identityProvider = resolveIdentityProvider(config);
		const derivationOrigin = resolveDerivationOrigin(config);
		return new AuthClient({
			identityProvider,
			...(derivationOrigin ? { derivationOrigin } : {}),
			idleOptions: { disableIdle: true },
		});
	});
	const [identity, setIdentity] = useState<Identity | null>(null);
	const [agent, setAgent] = useState<HttpAgent | null>(null);
	const [actor, setActor] = useState<ProtocolActor | null>(null);
	const [isInitializing, setInitializing] = useState(true);
	const [isLoggingIn, setLoggingIn] = useState(false);
	const [balances, setBalances] = useState<WalletBalances>(() =>
		hasProtocolEnv() ? ZERO_BALANCES : { ...FIXTURE.balances },
	);
	const [balancesLive, setBalancesLive] = useState(false);
	const [balancesLoading, setBalancesLoading] = useState(false);
	const [balancesError, setBalancesError] = useState<string | null>(null);
	const [actorError, setActorError] = useState<string | null>(null);
	const [sessionStatus, setSessionStatus] =
		useState<SessionStatus>("anonymous");
	const [protocolRole, setProtocolRole] = useState<ProtocolRole>("unknown");
	const promptShownRef = useRef(false);
	const identityRef = useRef(identity);
	identityRef.current = identity;

	const configError = useMemo(() => {
		if (!hasProtocolEnv()) {
			return "Protocol canister ID not set — rebuild SPA after start-local writes frontend-v2/.env.local (or use Vite with that file).";
		}
		return null;
	}, []);

	const reportSessionIssue = useCallback(
		(reason: "expired" | "degraded" | "unauthorized" = "degraded") => {
			if (reason === "expired") {
				setSessionStatus("expired");
				setAgent(null);
				setActor(null);
				promptShownRef.current = true;
				return;
			}
			// unauthorized / degraded: show reconnect once until dismissed or fixed
			if (sessionStatus === "expired") return;
			if (!promptShownRef.current) {
				promptShownRef.current = true;
				setSessionStatus("degraded_prompt");
			} else if (sessionStatus === "healthy" || sessionStatus === "degraded") {
				setSessionStatus("degraded_prompt");
			}
		},
		[sessionStatus],
	);

	const dismissSessionPrompt = useCallback(() => {
		if (sessionStatus === "expired") {
			// stay expired until login; just hide modal style by going anonymous path via logout optional
			setSessionStatus("expired");
			return;
		}
		if (sessionStatus === "degraded_prompt") {
			setSessionStatus("degraded");
		}
	}, [sessionStatus]);

	const refreshBalances = useCallback(async () => {
		if (!hasProtocolEnv() || !identity || !agent || !actor) {
			if (!hasProtocolEnv()) {
				setBalances({ ...FIXTURE.balances });
				setBalancesLive(false);
				setBalancesError(null);
			} else {
				setBalances(ZERO_BALANCES);
				setBalancesLive(false);
				setBalancesError(
					!identity
						? null
						: !actor
							? actorError ||
								getLastAttachError() ||
								"Protocol actor not ready — reconnect or log in again"
							: null,
				);
			}
			return;
		}
		const owner = identity.getPrincipal();
		if (owner.isAnonymous()) {
			setBalances(ZERO_BALANCES);
			setBalancesLive(false);
			setBalancesError(null);
			return;
		}
		setBalancesLoading(true);
		setBalancesError(null);
		try {
			const config = getConfig();
			const next = await fetchWalletBalances(actor, agent, config, owner);
			setBalances({
				USDM: next.USDM,
				ICP: next.ICP,
				ckBTC: next.ckBTC,
				ckETH: next.ckETH,
				MMK: next.MMK,
				ckUSDC: next.ckUSDC,
			});
			const errKeys = Object.keys(next.errors) as Array<
				keyof typeof next.errors
			>;
			const sum =
				next.USDM +
				next.ICP +
				next.ckBTC +
				next.ckETH +
				next.MMK +
				next.ckUSDC;
			const collMissing =
				!config.ledgers.icp && !config.ledgers.ckBtc && !config.ledgers.ckEth;
			if (errKeys.length > 0) {
				const parts = errKeys.map(
					(k) => `${k}: ${next.errors[k] ?? "failed"}`,
				);
				if (sum === 0) {
					setBalancesError(
						collMissing
							? `Balances empty and coll ledgers missing in SPA env. Re-run start-local without --no-demo-console, then rebuild SPA. ${parts.slice(0, 3).join("; ")}`
							: `Balance queries failed or zero (principal must match faucet/mint). ${parts.slice(0, 4).join("; ")}`,
					);
					setBalancesLive(errKeys.length < 6);
				} else {
					setBalancesError(
						`Some ledgers failed: ${parts.slice(0, 3).join("; ")}`,
					);
					setBalancesLive(true);
				}
			} else {
				setBalancesLive(true);
				if (collMissing) {
					setBalancesError(
						"Demo coll ledgers not in env (ICP/ck*) — run start-local without --no-demo-console, rebuild SPA",
					);
				} else if (sum === 0) {
					setBalancesError(null);
				}
			}
		} catch (err) {
			console.error("Balance fetch failed:", err);
			const msg =
				err instanceof Error ? err.message : String(err ?? "Balance fetch failed");
			setBalances(ZERO_BALANCES);
			setBalancesLive(false);
			setBalancesError(msg);
			const lower = msg.toLowerCase();
			if (
				lower.includes("unauthorized") ||
				lower.includes("invalid delegation") ||
				lower.includes("expired")
			) {
				reportSessionIssue("expired");
			}
		} finally {
			setBalancesLoading(false);
		}
	}, [identity, agent, actor, actorError, reportSessionIssue]);

	const attachProtocol = useCallback(
		async (
			id: Identity,
		): Promise<{ actor: ProtocolActor; agent: HttpAgent } | null> => {
			if (!hasProtocolEnv()) {
				const msg =
					"No protocol env in this SPA build — re-run start-local / spa:build with frontend-v2/.env.local";
				setActorError(msg);
				toast.error(msg);
				return null;
			}
			const pair = await getAuthenticatedActor(id);
			if (!pair) {
				const msg = actorNullMessage();
				setActorError(getLastAttachError() || msg);
				toast.error(msg);
				setAgent(null);
				setActor(null);
				setProtocolRole("unknown");
				setSessionStatus((s) =>
					s === "expired" ? s : id.getPrincipal().isAnonymous() ? "anonymous" : "degraded",
				);
				return null;
			}
			setAgent(pair.agent);
			setActor(pair.actor);
			setActorError(null);
			try {
				await pair.actor.initializeAccessControl();
			} catch (e) {
				console.warn("initializeAccessControl failed:", e);
				toast.message(
					"Connected, but protocol access setup failed — retry if vault actions error.",
				);
			}
			const role = await fetchRole(pair.actor);
			setProtocolRole(role);
			if (role === "guest") {
				setSessionStatus("degraded");
			} else {
				setSessionStatus("healthy");
				promptShownRef.current = false;
			}
			return { actor: pair.actor, agent: pair.agent };
		},
		[],
	);

	const reconnectProtocol = useCallback(async () => {
		if (!identity || identity.getPrincipal().isAnonymous()) {
			toast.error("Connect with Internet Identity first");
			return false;
		}
		const next = await attachProtocol(identity);
		if (next) {
			toast.success("Protocol actor reconnected");
			void refreshBalances();
			return true;
		}
		reportSessionIssue("degraded");
		return false;
	}, [identity, attachProtocol, refreshBalances, reportSessionIssue]);

	const ensureProtocolActor = useCallback(async () => {
		if (!hasProtocolEnv()) return null;
		if (actor && agent) return { actor, agent };
		if (!identity || identity.getPrincipal().isAnonymous()) return null;
		return attachProtocol(identity);
	}, [actor, agent, identity, attachProtocol]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				if (authClient.isAuthenticated()) {
					const id = await authClient.getIdentity();
					if (!cancelled && !id.getPrincipal().isAnonymous()) {
						setIdentity(id);
						if (hasProtocolEnv()) {
							const pair = await getAuthenticatedActor(id);
							if (pair && !cancelled) {
								setAgent(pair.agent);
								setActor(pair.actor);
								try {
									await pair.actor.initializeAccessControl();
								} catch (e) {
									console.warn("initializeAccessControl failed:", e);
								}
								const role = await fetchRole(pair.actor);
								if (!cancelled) {
									setProtocolRole(role);
									setSessionStatus(
										role === "guest" ? "degraded" : "healthy",
									);
								}
							} else if (!cancelled) {
								const err =
									getLastAttachError() || "session restore: actor null";
								console.warn("[motokomaker-v2]", err);
								setActorError(err);
								setSessionStatus("degraded");
							}
						} else if (!cancelled) {
							setSessionStatus("healthy");
						}
					}
				} else if (!cancelled) {
					setSessionStatus("anonymous");
				}
			} catch (err) {
				console.error("Auth init failed:", err);
			} finally {
				if (!cancelled) setInitializing(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [authClient]);

	useEffect(() => {
		void refreshBalances();
	}, [refreshBalances]);

	// Periodic session health: II still valid + actor still attached
	useEffect(() => {
		if (isInitializing) return;

		const check = async () => {
			const id = identityRef.current;
			if (!id || id.getPrincipal().isAnonymous()) {
				if (sessionStatus !== "anonymous" && sessionStatus !== "expired") {
					// no-op if already anon
				}
				return;
			}
			try {
				const still = authClient.isAuthenticated();
				if (!still) {
					setIdentity(null);
					setAgent(null);
					setActor(null);
					setProtocolRole("unknown");
					setSessionStatus("expired");
					promptShownRef.current = true;
					return;
				}
				const liveId = await authClient.getIdentity();
				if (liveId.getPrincipal().isAnonymous()) {
					setIdentity(null);
					setAgent(null);
					setActor(null);
					setSessionStatus("expired");
					promptShownRef.current = true;
					return;
				}
				// II ok but actor missing while env present → degraded
				if (hasProtocolEnv() && !actor && sessionStatus !== "expired") {
					if (!promptShownRef.current) {
						setSessionStatus("degraded_prompt");
						promptShownRef.current = true;
					} else if (sessionStatus === "healthy") {
						setSessionStatus("degraded");
					}
				}
			} catch {
				setSessionStatus("expired");
				promptShownRef.current = true;
			}
		};

		void check();
		const interval = window.setInterval(() => void check(), 60_000);
		const onFocus = () => void check();
		window.addEventListener("focus", onFocus);
		return () => {
			window.clearInterval(interval);
			window.removeEventListener("focus", onFocus);
		};
	}, [authClient, actor, isInitializing, sessionStatus]);

	const login = useCallback(async () => {
		setLoggingIn(true);
		try {
			const config = getIcConfig();
			const targets = config.protocolCanisterId
				? [Principal.fromText(config.protocolCanisterId)]
				: undefined;
			const id = await authClient.signIn({
				maxTimeToLive: sessionTtlNs(),
				...(targets ? { targets } : {}),
			});
			if (id.getPrincipal().isAnonymous()) {
				throw new Error("Internet Identity returned an anonymous principal");
			}
			setIdentity(id);
			promptShownRef.current = false;
			const next = await attachProtocol(id);
			if (next) {
				toast.success("Connected with Internet Identity");
			} else if (!hasProtocolEnv()) {
				setSessionStatus("healthy");
				toast.success(
					"Connected (II only) — SPA missing protocol env; balances/vaults offline",
				);
			}
		} catch (err) {
			toast.error(formatLoginError(err));
			throw err;
		} finally {
			setLoggingIn(false);
		}
	}, [authClient, attachProtocol]);

	const logout = useCallback(async () => {
		await authClient.signOut();
		setIdentity(null);
		setAgent(null);
		setActor(null);
		setProtocolRole("unknown");
		setSessionStatus("anonymous");
		promptShownRef.current = false;
		if (hasProtocolEnv()) {
			setBalances(ZERO_BALANCES);
			setBalancesLive(false);
		} else {
			setBalances({ ...FIXTURE.balances });
			setBalancesLive(false);
		}
		setBalancesError(null);
		setActorError(null);
		toast.message("Logged out");
	}, [authClient]);

	const principal =
		identity && !identity.getPrincipal().isAnonymous()
			? identity.getPrincipal().toText()
			: null;

	const value = useMemo(
		() => ({
			isAuthenticated: Boolean(principal),
			isInitializing,
			isLoggingIn,
			principal,
			identity,
			agent,
			actor,
			balances,
			balancesLive,
			balancesLoading,
			balancesError,
			actorError,
			sessionStatus,
			protocolRole,
			refreshBalances,
			reconnectProtocol,
			ensureProtocolActor,
			login,
			logout,
			configError,
			reportSessionIssue,
			dismissSessionPrompt,
		}),
		[
			principal,
			isInitializing,
			isLoggingIn,
			identity,
			agent,
			actor,
			balances,
			balancesLive,
			balancesLoading,
			balancesError,
			actorError,
			sessionStatus,
			protocolRole,
			refreshBalances,
			reconnectProtocol,
			ensureProtocolActor,
			login,
			logout,
			configError,
			reportSessionIssue,
			dismissSessionPrompt,
		],
	);

	return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth requires AuthProvider");
	return ctx;
}

/** Map liquidate / vault write errors to session recovery when auth-related. */
export function isAuthPermissionError(err: unknown): boolean {
	const msg = err instanceof Error ? err.message : String(err ?? "");
	const lower = msg.toLowerCase();
	return (
		lower.includes("user permission required") ||
		lower.includes("unauthorized") ||
		lower.includes("still a guest") ||
		lower.includes("invalid delegation")
	);
}
